import type { FileType } from "@1js/owl-shared-types";
import type {
  AppInitDiagnostics,
  MeetingInfo,
  DocRebootSupportMode,
} from "@1js/office-online-jsapi-core";
import type { WiseEnvironment } from "@1js/wise-environments";
import type { IWACPageLoadResult } from "../messages/messageTypes/Notification";
import type { BootDiagnostics as HostBootDiagnostics } from "@1js/owl-service";

export interface BootApplicationParams {
  documentBootInfo: DocumentBootInfo;
  sessionConfig: SessionConfig;
  uiHostConfig: UiHostConfig;
  getAuthToken: AccessTokenFunction;
  eventHandlers: EventHandlers;
  container: HTMLElement;
}

//TODO: Add appId or appName once settle down purpose with SPO
export interface DocumentBootInfo {
  docTenantUrl?: string; // document tenant url, used as baseUrl when construct .aspx url
  action: Action; // for owlet to constructing .aspx url
  sourceDoc?: string; // docId or docRelativePath for constructing .aspx url
  hostingPageOrigin: string; // the host origin that owlet embedded, indicates the msg posted origin for SPO to filter the msg originate.
  mobileRedirect?: boolean; // for owlet to constructing .aspx url
  driveId?: string;
  fileName?: string;
  shareId?: string;
  shareUrl?: string;
  baseUrl?: string;
  fileType?: FileType;
  clickTime?: number; // the time when user clicks on the document to open
  navId?: string; // navigation id, which is used for opening a PPT document to a specific slide
  wdValue?: string; //a string embeds in url indicates the keyworks
  wdOrigin?: string; // session origin
  uiCulture?: string; // ui language preference, for example: en-us
  dataCulture?: string; // data language preference, for example: en-gb
  puid?: string; // logged user id
  isNewDoc?: boolean; // indicates if the document is a new document
}

/**
 * Settings for configuring the document session, the data in this type
 * is specific to a document session and might change between sessions
 */
export interface SessionConfig {
  hostCorrelationId: string; // Ui Host Session Id
  wiseEnvironment: WiseEnvironment; // The WISEEnvironment that WISE should send telemetry to
  bootDiagnostics?: HostBootDiagnostics;
  isESSEnabled?: boolean;
  highContrastMode?: string;
  additionalApplicationParameters?: { [key: string]: string };
  upn?: string; // userPrincipalName
  meeting?: MeetingInfo;
  previousSession?: string;
  previousSessionSrc?: string;
  tenantId?: string; // current tenant id
  hostClientType?: string;
  isSynthetic?: boolean; // indicates if the session is a test session
}

/**
 * Settings for configuring behaviors or properties of the ui host, the ui host settings
 * in this type won't change between document sessions
 */
export interface UiHostConfig {
  hideHeader?: boolean;
  hideAppLauncherInHeader?: boolean;
  hideSearchInHeader?: boolean;
  hideMeInHeader?: boolean;
  hideChatInHeader?: boolean;
  hideWorkflowInHeader?: boolean;
  hideSettingsInHeader?: boolean;
  showAppIconInHeader?: boolean;
  useNeutralColorInHeader?: boolean;
  supportsAuthToken?: boolean;
  supportsAtMentions?: boolean;
  supportsAccessibilityLoop?: boolean;
  uiHostName?: string;
  sessionOriginPrefix?: string;
  supportsExcelExternalLinks?: boolean;
  showCloseButton?: boolean;
  openInBrowserEnabled?: boolean;
  supportsExplicitUnload?: boolean;
  supportsMoveToFolder?: boolean;
  supportsShare?: boolean;
  uiHostIntegrationType?: string;
  ellipsisDownloadACopyButtonEnabled?: boolean;
  openLinksInNewWindow?: boolean;
  copyLinkEnabled?: boolean;
  supportsCommonFilePicker?: boolean;
  supportsPopAndPushState?: boolean;
  supportsDirectPrint?: boolean;
  supportsDirectDownload?: boolean;
  contentAssemblyMode?:
    | "Templatization"
    | "DocGen"
    | "ContentAssemblyEdit"
    | "ContentAssemblyView";
  uiEmbed?: boolean;
  supportsHostUiControls?: boolean;
  supportsInternalReboot?: boolean;
  disableOfficeAddins?: boolean;
  supportsCreateNewDocument?: boolean;
  supportsDocumentReboot?: number | DocRebootSupportMode;
  supportsInPlaceCreateNew?: boolean;
  supportsEmbedViewCodeSnippet?: boolean;
  disableCoEditInTeams?: boolean;
}

export interface AccessTokenFunction {
  (resourceUrl: string, claims?: string[]): Promise<string>;
}

export interface EventHandlers {
  onBootComplete?: (
    docInfo: DocumentBootInfo,
    wacBootDiagnostics?: AppInitDiagnostics
  ) => void;
  onBootFailure?: (
    errorInfo?: BootFailedError,
    docInfo?: DocumentBootInfo
  ) => void;
  // handle keyboard focus in host frame once received the accessibility loop end indicator from wac
  onReturnFocus?: (navigateForward?: boolean) => void;
  // handle session end/tab closed/document closed event
  onDocumentClose?: () => void;
  // handle document renamed msg from WAC
  onRename?: (newName: string) => void;
}

export interface OwletEventHandlers extends EventHandlers {
  onRebootDocument: (
    newDocBootUrl: string,
    eventHandlers: OwletEventHandlers,
    rebootReason?: string,
    rebootMode?: string
  ) => void;
  onPageLoaded: (
    hostGeoRegion: string,
    pageLoadResult: IWACPageLoadResult
  ) => void;
}

/**
 * ErrorInfo format that OWLET expect SPO to return via post msg, or self construct by
 * owlet based on detailed failure
 */
export interface BootFailedError {
  errorMessage: string;
  errorName?: string;
  errorType?: ErrorType;
  errorDetail?: ErrorDetail;
}

export interface ErrorDetail {
  httpErrorCode?: string;
  wacSessionId?: string;
}

export enum ErrorType {
  NetworkError = "NetworkError",
  HttpError = "HttpError",
  AuthError = "AuthError",
  InnerFrameScriptError = "InlineFrameScriptError",
  Timeout = "Timeout",
  ExpectedFailure = "ExpectedFailure",
  InvalidConfiguration = "InvalidConfiguration",
}

// Identifies indicates the mode that document will be open
export enum Action {
  Default = "Default",
  Edit = "Edit",
  View = "View",
  EmbedView = "EmbedView",
  EmbedEdit = "EmbedEdit",
  InteractivePreview = "InteractivePreview ",
  ImagePreview = "ImagePreview ",
  MobileView = "MobileView ",
  EditNew = "editNew",
}

//Diagnostic metadata returned to UI Host
export interface BootDiagnostics {
  hostCorrelationId: string;
  isBootSuccess: Boolean;
  errorInfo?: BootFailedError;
  packageEntryTime?: number;
  bootApplicationStartTime?: number;
  bootApplicationFinishedTime?: number;
}

// Metadata to be provided to SPO in the scenario of opening a document via shareUrl
export interface ShareUrlBootDocInfo {
  siteUrl: string; // document href location in SPO sites
  listItemUniqueId: string; // document identifier in SPO
  vroomAccessToken: string;
  appName?: any;
}

export enum KpiMetricName {
  KPI_OwletBoot = "OwletBoot",
}

export enum QosErrorName {
  QosError_OwletBootApplicationFailure = "OwletBootApplicationFailure",
  QosError_OwletVroomGetDriveItemFailure = "OwletVroomGetDriveItemFailure",
  QosError_OwletFetchAccessTokenFailure = "OwletFetchAccessTokenFailure",
}

export enum QosVetoName {
  QosVeto_BootDocumentExperienceFailed = "BootDocumentExperienceFailed",
  QosVeto_GetDriveItemFailed = "GetDriveItemFailed",
  QosVeto_AccessTokenRetrieveFailed = "AccessTokenRetrieveFailed",
}

export enum OwningFeatureCrewAlias {
  FC_Owl = "owlfc",
}
